import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Lock, User, Phone, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'passenger',
    country: 'MX',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData);
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#00BFA6]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FFD166]/10 rounded-full blur-3xl" />

      {/* Logo */}
      <div className="flex flex-col items-center mb-6 animate-slide-up">
        <img 
          src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
          alt="Mercapoli Move"
          className="h-12"
        />
      </div>

      {/* Register Card */}
      <Card className="w-full max-w-md card-elevated animate-slide-up" style={{ animationDelay: '0.1s' }}>
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-bold text-slate-800 font-['Outfit']">
            Crear Cuenta
          </CardTitle>
          <CardDescription className="text-slate-500">
            Únete a la revolución de la movilidad
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-slate-700">Nombre completo</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="full_name"
                  type="text"
                  placeholder="Juan Pérez"
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  required
                  className="pl-12 h-12 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-xl focus:border-[#00BFA6]"
                  data-testid="fullname-input"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  className="pl-12 h-12 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-xl focus:border-[#00BFA6]"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700">Teléfono</Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+52 555 123 4567"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  className="pl-12 h-12 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-xl focus:border-[#00BFA6]"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required
                  minLength={6}
                  className="pl-12 h-12 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-xl focus:border-[#00BFA6]"
                  data-testid="password-input"
                />
              </div>
            </div>

            {/* Role & Country Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Tipo de cuenta</Label>
                <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                  <SelectTrigger className="h-12 bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="role-select">
                    <SelectValue placeholder="Rol" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="passenger" className="text-slate-800">Pasajero</SelectItem>
                    <SelectItem value="driver" className="text-slate-800">Conductor</SelectItem>
                    <SelectItem value="vehicle_owner" className="text-slate-800">Dueño de vehículo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700">País</Label>
                <Select value={formData.country} onValueChange={(v) => handleChange('country', v)}>
                  <SelectTrigger className="h-12 bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="country-select">
                    <SelectValue placeholder="País" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="MX" className="text-slate-800">México</SelectItem>
                    <SelectItem value="CO" className="text-slate-800">Colombia</SelectItem>
                    <SelectItem value="PE" className="text-slate-800">Perú</SelectItem>
                    <SelectItem value="US" className="text-slate-800">USA</SelectItem>
                    <SelectItem value="BR" className="text-slate-800">Brasil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 btn-primary text-lg mt-6"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Crear cuenta
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-500">
              ¿Ya tienes cuenta?{' '}
              <Link 
                to="/login" 
                className="text-[#00BFA6] hover:text-[#00887A] font-medium transition-colors"
                data-testid="login-link"
              >
                Inicia sesión
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
